/** What belongs in the conversation, what belongs in the audit trail, and what the
 *  raw event names mean in English.
 *
 *  The drawer used to show one list: every history row, labelled with its enum name.
 *  That list is the artifact the whole data model exists to serve and it is staying —
 *  but it answers "what happened to this task", and a question somebody is waiting on
 *  answers "what are we saying to each other". Rendering both in one column meant a
 *  remark appeared as `REMARK_ADDED` above a quote block, which is an audit row
 *  pretending to be a message.
 *
 *  So the panel splits, and the split has one rule: **a message-bearing event appears
 *  in the conversation and nowhere else.** Showing it in both would be two lists of
 *  the same thing, which is the failure this file exists to prevent — hence the single
 *  exported set rather than a condition written out at each list.
 */

/** Events whose whole content is something a person said. These render in the
 *  conversation, built from `task_messages`, and are filtered out of the timeline.
 *
 *  Add a message-bearing event here the moment one is introduced, or it will show up
 *  twice — once as a line of talk and once as an audit row with the same words in it. */
export const MESSAGE_EVENTS = new Set([
  'REMARK_ADDED',
  // Not emitted yet - the query work is a later stage - but listed now so the two
  // halves of that change cannot be made separately and leave a question rendered in
  // both columns.
  'QUERY_RAISED', 'QUERY_ANSWERED', 'QUERY_TRANSFERRED', 'QUERY_WITHDRAWN',
])

/** The enum name, in English.
 *
 *  These were rendered raw — `COMPLETION_SUBMITTED` in a bold sans face, on the one
 *  panel non-technical staff are most likely to read closely. The words are the
 *  firm's own wherever the firm has one: a task is "sent for approval", not
 *  "submitted", and work comes "back for rework" rather than being "rejected",
 *  because REJECTED is an event whose resting state is REWORK and the trail should
 *  not imply the task stopped there.
 */
export const EVENT_LABEL: Record<string, string> = {
  CREATED: 'Raised',
  ASSIGNED: 'Assigned',
  REASSIGNED: 'People changed',
  EDITED: 'Edited',
  REMARK_ADDED: 'Remark',
  COMPLETION_SUBMITTED: 'Sent for approval',
  COMPLETED: 'Completed',
  APPROVAL_REQUESTED: 'Approval requested',
  APPROVED: 'Approved',
  REJECTED: 'Sent back for rework',
  REWORK_STARTED: 'Rework started',
  CARRIED_FORWARD: 'Carried forward',
  DAY_OPENED: 'Gathered onto a reopened day',
  DAY_CLOSED: 'Day closed',
  DAY_REOPENED: 'Day reopened',
  CANCELLED: 'Dropped',
  REOPENED: 'Reopened',
  NOTIFICATION_SENT: 'Notified',
  NOTIFICATION_FAILED: 'Could not notify',
}

/** An event's label, falling back to the raw name.
 *
 *  The fallback is deliberate rather than a `??` waiting to be tidied away: the
 *  backend can add an event and deploy before the web app does, and an unlabelled
 *  `QUERY_TRANSFERRED` in the trail is worth far more than a blank row or a crash. */
export function eventLabel(type: string): string {
  return EVENT_LABEL[type] ?? type
}

/** The timeline, with the talking taken out. */
export function auditOnly<T extends { event_type: string }>(events: T[]): T[] {
  return events.filter((e) => !MESSAGE_EVENTS.has(e.event_type))
}

/** The question this task is waiting on, if it is waiting on one.
 *
 *  At most one is expected in practice — raising a second while the first is open is
 *  refused server-side — but this reads the list rather than assuming, and takes the
 *  earliest, so a data state nobody meant to create still shows the question that has
 *  been waiting longest rather than picking arbitrarily. */
export function openQuestion<T extends { needs_answer: string; state: string }>(
  messages: T[],
): T | null {
  return messages.find((m) => m.needs_answer === 'Yes' && m.state === 'open') ?? null
}

/** May this person answer the open question?
 *
 *  The person it was put to, or an owner — who can always unstick something. The
 *  server decides for real; this only decides whether to draw the button, and drawing
 *  one that answers 403 is the thing to avoid. */
export function mayAnswer(
  question: { addressee_user_id: string } | null,
  me: { user_id: string; is_owner: boolean } | null | undefined,
): boolean {
  if (!question || !me) return false
  return me.is_owner || question.addressee_user_id === me.user_id
}
